/**
 * Formats AI explanation text for better readability in the modal
 */
export const formatExplanation = (text: string): string => {
    if (!text) {return '';}
    
    // Remove HTML tags if present
    const cleanText = text.replace(/<[^>]*>/g, '');
    
    // Split into lines and process each line
    const lines = cleanText.split('\n');
    let formatted = '';
    let previousWasNumbered = false;
    let previousWasBullet = false;
    
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        
        if (!line) {
            // Only add one line break for empty lines
            if (formatted && !formatted.endsWith('\n')) {
                formatted += '\n';
            }
            continue;
        }
        
        // Handle numbered lists (1., 2., 3., etc.)
        if (/^\d+\.\s/.test(line)) {
            // Add spacing before first numbered item or after non-numbered content
            if (!previousWasNumbered && formatted && !formatted.endsWith('\n\n')) {
                formatted += '\n\n';
            } else if (previousWasNumbered && formatted && !formatted.endsWith('\n')) {
                formatted += '\n\n';  // More spacing between numbered items
            }
            formatted += line;
            previousWasNumbered = true;
            previousWasBullet = false;
            continue;
        }
        
        // Handle bullet points
        if (/^[-•*]\s/.test(line)) {
            // Add spacing before first bullet or after non-bullet content
            if (!previousWasBullet && formatted && !formatted.endsWith('\n\n')) {
                formatted += '\n';
            } else if (previousWasBullet && formatted && !formatted.endsWith('\n')) {
                formatted += '\n';
            }
            formatted += '• ' + line.replace(/^[-•*]\s/, '');
            previousWasBullet = true;
            previousWasNumbered = false;
            continue;
        }
        
        // Handle regular text
        if (previousWasNumbered || previousWasBullet) {
            formatted += '\n\n';
        } else if (formatted && !formatted.endsWith('\n') && !formatted.endsWith(' ')) {
            formatted += ' ';
        }
        
        formatted += line;
        previousWasNumbered = false;
        previousWasBullet = false;
        
        // Add line break after sentences that end with period, question mark, or exclamation
        if (/[.!?]$/.test(line) && i < lines.length - 1 && lines[i + 1].trim()) {
            const nextLine = lines[i + 1].trim();
            // Don't add extra breaks if next line is numbered or bullet
            if (!/^\d+\.\s/.test(nextLine) && !/^[-•*]\s/.test(nextLine)) {
                formatted += '\n\n';
            }
        }
    }
    
    // Clean up multiple consecutive line breaks
    formatted = formatted.replace(/\n{3,}/g, '\n\n');
    
    // Clean up excessive whitespace at start/end
    formatted = formatted.trim();
    
    return formatted;
};
